import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Zap } from "lucide-react";

const TRIALS = 5;

export function ReactionTime({ onScore }) {
  const [phase, setPhase] = useState("idle"); // idle | wait | go | early | trial-done | done
  const [times, setTimes] = useState([]);
  const [last, setLast] = useState(null);
  const goAt = useRef(0);
  const timer = useRef(null);
  const phaseRef = useRef("idle");
  const reported = useRef(false);

  const setPh = (p) => { phaseRef.current = p; setPhase(p); };

  const nextTrial = useCallback(() => {
    setPh("wait");
    timer.current = setTimeout(() => { goAt.current = performance.now(); setPh("go"); }, 1200 + Math.random() * 2500);
  }, []);

  const start = () => { setTimes([]); setLast(null); reported.current = false; nextTrial(); };

  const click = () => {
    const ph = phaseRef.current;
    if (ph === "wait") {
      clearTimeout(timer.current);
      setPh("early");
    } else if (ph === "go") {
      const rt = Math.round(performance.now() - goAt.current);
      setLast(rt);
      const nt = [...times, rt];
      setTimes(nt);
      if (nt.length >= TRIALS) setPh("done");
      else setPh("trial-done");
    } else if (ph === "early") {
      nextTrial();
    } else if (ph === "trial-done") {
      nextTrial();
    }
  };

  const avg = times.length ? Math.round(times.reduce((a, b) => a + b, 0) / times.length) : 0;

  useEffect(() => {
    if (phase === "done" && !reported.current) {
      reported.current = true;
      onScore?.(Math.max(50, 600 - avg)); // faster = higher
    }
  }, [phase, avg, onScore]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const bg = phase === "go" ? "#39FF14" : phase === "wait" ? "#FF003C" : phase === "early" ? "#FF003C" : "#18181b";

  return (
    <div className="w-full max-w-md mx-auto" data-testid="reaction-time">
      <div className="flex items-center justify-between mb-4 font-mono text-sm">
        <span className="text-neon-cyan">TRIAL: {Math.min(times.length + (phase === "done" ? 0 : 1), TRIALS)}/{TRIALS}</span>
        {avg > 0 && <span className="text-neon-lime">AVG: {avg}ms</span>}
      </div>

      <button onClick={click} data-testid="reaction-pad"
        className="w-full rounded-2xl border-2 border-zinc-700 flex flex-col items-center justify-center text-center px-6 transition-colors duration-75"
        style={{ background: bg, minHeight: 260 }}>
        {phase === "idle" && (
          <>
            <Zap className="w-10 h-10 text-neon-yellow mb-2" />
            <p className="text-white font-display font-black text-xl uppercase">Test Your Reflexes</p>
            <p className="text-zinc-400 text-sm mt-1">5 trials. Click the instant it turns green.</p>
          </>
        )}
        {phase === "wait" && <p className="text-white font-display font-black text-2xl uppercase">Wait for green…</p>}
        {phase === "go" && <p className="text-black font-display font-black text-4xl uppercase">CLICK!</p>}
        {phase === "early" && <p className="text-white font-display font-black text-xl uppercase">Too soon! Click to retry</p>}
        {phase === "trial-done" && (
          <>
            <p className="text-neon-cyan font-display font-black text-4xl" data-testid="reaction-last">{last}ms</p>
            <p className="text-zinc-300 text-sm mt-1">Click to continue</p>
          </>
        )}
        {phase === "done" && (
          <>
            <p className="text-zinc-300 uppercase text-xs tracking-widest">Average reaction</p>
            <p className="text-neon-lime font-display font-black text-5xl" data-testid="reaction-avg">{avg}ms</p>
            <p className="text-zinc-400 text-sm mt-1">{avg < 250 ? "Lightning fast! ⚡" : avg < 350 ? "Great reflexes!" : "Keep practicing!"}</p>
          </>
        )}
      </button>

      {(phase === "idle" || phase === "done") && (
        <div className="text-center mt-4">
          <Button onClick={start} data-testid="reaction-start" className="bg-neon-cyan text-black rounded-none font-bold uppercase tracking-widest">
            {phase === "done" ? "Test Again" : "Start"}
          </Button>
        </div>
      )}
    </div>
  );
}
